import { useCallback, useMemo } from 'react';
import {
	AIAgentResponseState,
	type AIAgentStreamingRequest,
	type AIAgentStreamingState,
	ErrorMessage,
	useAIAgentStreaming,
} from '@atlassian/ai-agent-streaming';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { sendExperienceAnalytics } from '@atlassian/jira-issue-view-analytics/src/controllers/send-experience-analytics/index.tsx';
import { JIRA_SOFTWARE } from '@atlassian/jira-shared-types/src/application.tsx';
import { PREMIUM_EDITION } from '@atlassian/jira-shared-types/src/edition.tsx';
import { useNativeLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import getMeta from '@atlassian/jira-get-meta';
import { useSmartSummarizeCommentsAnalytics } from '../../common/analytics/index.tsx';

const isSynthetic = () => getMeta('ajs-is-synthetic') === 'true';

const SMART_SUMMARY_REQUEST_HEADERS = {
	'x-experience-id': 'jira-smart-summary',
	'x-product': 'jira',
	'x-issynthetic-traffic': isSynthetic().toString(),
};

// The status code belongs to this list won't be sent as taskFail
const STATUS_CODE_RELIABILITY_WHITE_LIST = [400, 401, 403, 429];

type SmartSummaryInputContext = {
	content_url: string;
	summary_style: string;
	summary_output_mimetype: string;
	prompt_id: string;
	locale: string;
};

const sendSloMetrics = (
	wasExperienceSuccesful: boolean,
	errorMessage?: string,
	statusCode?: number,
) => {
	const experience = 'jiraSmartSummarizeComments';
	const analyticsSource = 'requestSummaryQuery';
	const additionalAttributes = {
		...(errorMessage && { error: errorMessage }),
		...(statusCode && { statusCode }),
	};
	sendExperienceAnalytics({
		experience,
		wasExperienceSuccesful,
		analyticsSource,
		application: JIRA_SOFTWARE,
		edition: PREMIUM_EDITION,
		additionalAttributes,
	});
};

export const useSmartSummaryStreaming = () => {
	const issueKey = useIssueKey();
	const locale = useNativeLocale();
	const { fireTrack } = useSmartSummarizeCommentsAnalytics();

	const agentPayload = useMemo(
		() => ({
			recipient_agent_named_id: 'summary_agent',
			agent_input_prompt:
				'Summarise the description and comments in this ticket, focus on the essential context points, key decisions made, and specified action items',
			agent_input_context: {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				content_url: `${window.location.origin}/browse/${issueKey}`,
				summary_style: 'medium',
				summary_output_mimetype: 'text/markdown',
				prompt_id: 'jira-smart-summary',
				locale,
			},
		}),
		[issueKey, locale],
	);

	const onStateChange = useCallback(
		(state: AIAgentStreamingState<string>) => {
			// TODO: add potential analytics/peformance markers and potential state changes for ui?
			switch (state.responseState) {
				case AIAgentResponseState.Initialization:
					// first call with AS and establish stream. no streamed events yet
					break;
				case AIAgentResponseState.AnswerPart:
					break;
				case AIAgentResponseState.FinalResponse:
					fireTrack('aiResult viewed', 'jiraSmartSummarizeComments', {
						metadata: state.metadata.tokenData,
					});
					sendSloMetrics(true);
					break;
				case AIAgentResponseState.Error: {
					const errorMessage = state.error?.message.content;
					const errorStatusCode = state.error?.message.status_code || 500;
					const errorMessageTemplate = state.error?.message.message_template;

					// this is user content, we do not classify this as an error for both SLO and unified error analytics
					if (errorMessageTemplate === ErrorMessage.ACCEPTABLE_USE_VIOLATIONS) {
						break;
					}

					/**
					 * Do not trigger SLO failures if they are part of these status code white lists
					 */
					if (!STATUS_CODE_RELIABILITY_WHITE_LIST.includes(errorStatusCode)) {
						sendSloMetrics(false, errorMessage, errorStatusCode);
					}

					fireTrack('aiResult error', 'jiraSmartSummarizeCommentsError', {
						error: errorMessage,
						statusCode: errorStatusCode,
					});
					break;
				}
				default:
					break;
			}
		},
		[fireTrack],
	);

	const [state, fetch] = useAIAgentStreaming<
		AIAgentStreamingRequest<SmartSummaryInputContext>,
		string
	>(
		agentPayload,
		{
			headers: SMART_SUMMARY_REQUEST_HEADERS,
		},
		onStateChange,
	);

	return [state, fetch] as const;
};
